import React from 'react';
import './customer-card.scss';
import { getSelectedItemIds, useAppSelector } from '@zonofi/zono-money-store';
import { TouchableOpacity } from '../../common';
import { SVGS } from '../../assets';
import { Avatar, Divider } from '../../components';
import { DividerColorVariant, SelectionModeType } from '../../constants';
import {
  TAGS_MAP,
  TagsIcon,
  type TagsKey,
} from '../../conversations/contstants';
import { ReactComponent as ArrowRight } from '@zonofi/svg-icons/arrow_right.svg';
import { ReactComponent as Checkbox } from '@zonofi/svg-icons/checkbox.svg';
import { ReactComponent as Checked } from '@zonofi/svg-icons/checked.svg';
import { ReactComponent as PhoneIcon } from '@zonofi/svg-icons/phone-icon.svg';

interface CustomerCardProps {
  customer: any;
  onCustomerSelect?: (customer: any) => void;
  onCheckboxToggle?: (customer: any) => void;
  isFirst?: boolean;
  isLast?: boolean;
  selectionMode?: SelectionModeType;
  selectedItem?: boolean;
  titleKey?: string;
  fallbackKeys?: string[];
  dividerStyle?: string;
  needArrowRight?: boolean;
  idToCompare?: string;
  isExcluded?: boolean;
  showCallBtn?: boolean;
  showConversationInsights?: boolean;
}

interface Tag {
  label: string;
  type: 'status' | 'segment' | 'route';
  icon?: string;
  color?: string;
}

const TAG_VARIANT_LIMIT = 2;
const LIFECYCLE_COLOR_PALETTE = [
  '#2abb7f',
  '#bc2e0a',
  '#0067ff',
  '#8c3cff',
  '#2abb7f',
  '#916100',
  '#00a3bf',
  '#127954',
];

const getLifecycleTagColor = (group: any): string => {
  const serverColor =
    group?.color ?? group?.colour ?? group?.colorCode ?? group?.groupColor;
  if (typeof serverColor === 'string' && serverColor.trim()) {
    return serverColor;
  }

  const name = String(group?.name ?? '');
  if (!name) return '#595959';

  const hash = name
    .toLowerCase()
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return LIFECYCLE_COLOR_PALETTE[hash % LIFECYCLE_COLOR_PALETTE.length];
};

export const CustomerCard: React.FC<CustomerCardProps> = ({
  customer,
  onCustomerSelect,
  onCheckboxToggle,
  isFirst = false,
  isLast = false,
  selectionMode = 'multiple',
  selectedItem = false,
  titleKey = '',
  fallbackKeys = [''],
  dividerStyle,
  needArrowRight = true,
  idToCompare = 'inviteId',
  isExcluded = false,
  showCallBtn = false,
  showConversationInsights = false,
}) => {
  const getDisplayTitle = (data: any): string => {
    if (data[titleKey]) return data[titleKey];
    for (const key of fallbackKeys) {
      if (data[key]) return data[key];
    }

    return 'N/A';
  };
  const excludedCustomerIds = useAppSelector(getSelectedItemIds);
  const isSelected =
    selectionMode === 'multiple'
      ? excludedCustomerIds?.includes(customer?.[idToCompare])
      : selectedItem;

  const handleCallCustomer = () => {
    const phone = String(customer?.phone || '').replace(/\s/g, '');
    if (!phone) return;

    const formattedPhone = phone.startsWith('+') ? phone : `+${phone}`;
    window.location.href = `tel:${formattedPhone}`;
  };

  const buildTags = (): Tag[] => {
    // const tags: Tag[] = [];
    // TODO: remove this when data is available
    const tags: Tag[] = [
      ...(customer?.customerLifeCycleGroups ?? [])
        ?.slice(0, 1)
        ?.map((group: any) => ({
          label: group.name,
          type: 'status',
          icon: group.emojiId,
          color: getLifecycleTagColor(group),
        })),
    ];

    // Location tags: prefer CFA supplier names when includeCFADivision is enabled.
    const segmentRows = customer?.customer_segment ?? [];
    const routeRows = customer?.routes ?? [];

    const customerSegmentLabels = segmentRows
      .map((customerSegment: any) => customerSegment?.name)
      .filter((label: string | undefined): label is string =>
        Boolean(label?.trim())
      )
      .map((label: string) => ({ label, type: 'segment' as const }));

    const customerRouteLabels = routeRows
      .map((customerRoute: any) => customerRoute?.name)
      .filter((label: string | undefined): label is string =>
        Boolean(label?.trim())
      )
      .map((label: string) => ({ label, type: 'route' as const }));

    tags.push(...customerRouteLabels, ...customerSegmentLabels);

    return tags;
  };

  const allTags = buildTags();

  const visibleTags = allTags.slice(0, TAG_VARIANT_LIMIT + 1);
  const extraCount = allTags.length - visibleTags.length;

  const formatAmount = (amount: number | string) => {
    const numericAmount = Number(amount || 0);
    if (Number.isNaN(numericAmount) || numericAmount === 0) return null;
    const formatDRCR = numericAmount > 0 ? 'DR ' : 'CR ';
    return formatDRCR + `₹${Math.abs(numericAmount).toLocaleString('en-IN')}`;
  };

  const toPayAmount = formatAmount(
    customer?.toPay ?? customer?.outstandingAmount
  );
  const lastInvoicedDate =
    customer?.lastInvoicedDate ?? customer?.lastInvoicedOn;

  const getCustomerSummary = () => {
    const conversationData = customer?.conversationData;
    const firstConversation = Array.isArray(conversationData)
      ? conversationData[0]
      : conversationData;

    return (
      firstConversation?.summary ||
      firstConversation?.conversationSummary ||
      customer?.conversationSummary ||
      customer?.summary ||
      customer?.latestConversation?.summary ||
      customer?.conversation?.summary ||
      ''
    );
  };

  const getCustomerMetaTags = (): string[] => {
    const conversationData = customer?.conversationData;
    const firstConversation = Array.isArray(conversationData)
      ? conversationData[0]
      : conversationData;

    const rawTags =
      firstConversation?.metaTags ||
      firstConversation?.conversationTags ||
      firstConversation?.tags ||
      customer?.metaTags ||
      customer?.conversationTags ||
      customer?.latestConversation?.tags ||
      customer?.conversation?.tags ||
      [];

    const normalizedTags = Array.isArray(rawTags)
      ? rawTags
      : typeof rawTags === 'string'
        ? rawTags.split(',')
        : [];

    return normalizedTags.filter(
      (tag: unknown): tag is string =>
        typeof tag === 'string' && Boolean(tag.trim())
    );
  };

  const conversationSummary = getCustomerSummary();
  const conversationMetaTags = getCustomerMetaTags().slice(0, 3);

  const findMetaTagKey = (tag: string): TagsKey => {
    const normalizedTag = String(tag).toLowerCase().trim().replace(/\s+/g, '_');

    if (normalizedTag.includes('urgent')) return 'urgent';

    const matchedEntry = (
      Object.entries(TAGS_MAP) as Array<[TagsKey, string[]]>
    ).find(([, values]) =>
      values.some((value) => value.toLowerCase() === normalizedTag)
    );

    return matchedEntry?.[0] ?? 'custom';
  };

  const getMetaTagIcon = (tagKey: TagsKey) => {
    return <span>{TagsIcon[tagKey]}</span>;
  };

  return (
    <div
      className={`active-customer-card
        ${isFirst ? 'first-card' : ''}
        ${isLast ? 'last-card' : ''}`}
    >
      <div
        className={`active-customer-card-content ${
          isLast ? 'last-card-margin' : ''
        }`}
      >
        {selectionMode !== 'null' && (
          <TouchableOpacity
            className="customer-checkbox"
            onPress={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onCheckboxToggle?.(customer);
            }}
            style={{ marginRight: 8 }}
          >
            {selectionMode === 'multiple' &&
              (isSelected ? (
                <Checked color="#000000" />
              ) : (
                <Checkbox color="#8C8C8C" />
              ))}
            {selectionMode === 'single' && (
              <img
                src={isSelected ? SVGS.SelectedRadio : SVGS.CircleUnselected}
                width={28}
                height={28}
                alt="radiobutton"
              />
            )}
          </TouchableOpacity>
        )}

        <Avatar
          profileUrl=""
          id={customer?.inviteId}
          title={getDisplayTitle(customer)}
          size={36}
          style={{ marginRight: '12px' }}
        />
        <TouchableOpacity
          className="customer-details"
          onPress={() => onCustomerSelect?.(customer)}
        >
          <div className="customer-details-inner">
            {/* Name row */}
            <div className="customer-details-card">
              <span className="customer-details-name font-semibold">
                {getDisplayTitle(customer)}
              </span>
              {isExcluded && (
                <img
                  height={16}
                  width={16}
                  src={SVGS.ExcludeIcon}
                  style={{ marginLeft: '4px' }}
                />
              )}
              {customer?.isActive === 0 && (
                <img
                  height={16}
                  width={16}
                  src={SVGS.LockIcon}
                  style={{ marginLeft: '4px' }}
                />
              )}
              {customer?.excludedAndBlockedBotAudience?.isArchived === true && (
                <img
                  height={16}
                  width={16}
                  src={SVGS.NoWhatsApp}
                  style={{ marginLeft: '4px' }}
                />
              )}
            </div>

            {showConversationInsights && conversationSummary && (
              <div className="customer-details-summary font-regular">
                {conversationSummary}
              </div>
            )}

            {showConversationInsights && conversationMetaTags.length > 0 && (
              <div className="customer-meta-tags-row">
                {conversationMetaTags.map((tag) => {
                  const tagKey = findMetaTagKey(tag);

                  return (
                    <span
                      key={`${customer?.id || customer?.inviteId}-${tag}`}
                      className={`customer-meta-tag customer-meta-tag--${tagKey} font-semibold`}
                    >
                      <span className="customer-meta-tag-icon">
                        {getMetaTagIcon(tagKey)}
                      </span>
                      {tag}
                    </span>
                  );
                })}
              </div>
            )}

            {(toPayAmount || lastInvoicedDate) && (
              <div className="customer-details-meta font-regular">
                {toPayAmount && (
                  <span>
                    To Pay:{' '}
                    <span className="customer-details-meta--amount">
                      {toPayAmount}
                    </span>
                  </span>
                )}
                {toPayAmount && lastInvoicedDate && (
                  <span className="customer-details-meta--dot"> • </span>
                )}
                {lastInvoicedDate && (
                  <span>Last Invoiced on {lastInvoicedDate}</span>
                )}
              </div>
            )}

            {/* Tags */}
            {allTags.length > 0 && (
              <div className="customer-tags-row">
                {visibleTags.map((tag, i) => {
                  const prevTag = visibleTags[i - 1];

                  const showDivider =
                    i > 0 &&
                    prevTag?.type === 'status' &&
                    (tag.type === 'segment' || tag.type === 'route');

                  return (
                    <React.Fragment key={i}>
                      {showDivider && <span className="customer-tag-divider" />}

                      <span
                        className={`customer-tag font-semibold customer-tag--${
                          tag.type === 'status' ? 'status' : 'location'
                        }`}
                        style={
                          tag.type === 'status'
                            ? {
                                background: tag.color ?? '#595959',
                                color: '#fff',
                              }
                            : undefined
                        }
                      >
                        {tag.type === 'status' && tag.icon && (
                          <span className="lifecycle-chip-icon">
                            {tag.icon}
                          </span>
                        )}
                        {/* {" "} */}
                        {tag.type === 'route' && (
                          <img height={12} width={12} src={SVGS.MapIcon} />
                        )}
                        {tag.type === 'segment' && (
                          <img height={12} width={12} src={SVGS.segmentIcon} />
                        )}

                        {tag.label}
                      </span>
                    </React.Fragment>
                  );
                })}

                {extraCount > 0 && (
                  <span className="customer-tag customer-tag--more font-semibold">
                    +{extraCount}
                  </span>
                )}
              </div>
            )}
          </div>
        </TouchableOpacity>
        {(showCallBtn || needArrowRight) && (
          <div className="customer-actions">
            {showCallBtn && !!customer?.phone && (
              <TouchableOpacity
                className="customer-call-btn"
                onPress={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCallCustomer();
                }}
              >
                <PhoneIcon color="#32A7E8" width={20} height={20} />
              </TouchableOpacity>
            )}
            {needArrowRight && (
              <ArrowRight color="#8C8C8C" width={20} height={20} />
            )}
          </div>
        )}
      </div>

      {!isLast && (
        <Divider
          className={`customer-divider-view ${dividerStyle}`}
          colorVariant={DividerColorVariant.PRIMARY}
        />
      )}
    </div>
  );
};
